import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { APP_NAME } from "../config/branding";
import Seo from "../components/Seo";

const Terms = () => {
  return (
    <div className="min-h-screen bg-background">
      <Seo
        title="Terms of Service | CBAIQ"
        description="CBAIQ Terms of Service. CBAIQ is a decision-support platform for understanding collective bargaining agreements — not a law firm and not legal advice."
        path="/terms"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="prose prose-slate dark:prose-invert max-w-none">
          <h1 className="text-3xl font-bold mb-2">{APP_NAME} – Terms of Service</h1>
          <p className="text-muted-foreground mb-8">Effective Date: January 2026</p>

          <div className="space-y-6">
            <section>
              <p>
                {APP_NAME} is a decision-support platform designed to help users understand Collective Bargaining Agreements (CBAs). 
                {APP_NAME} is not a law firm, does not provide legal advice, and does not replace legal counsel.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">{APP_NAME} is NOT:</h2>
              <ul className="list-disc list-inside space-y-2">
                <li>A lawyer</li>
                <li>A union representative</li>
                <li>A legal authority</li>
                <li>A compliance guarantee</li>
              </ul>
              <p className="mt-3">
                <strong>All outputs are informational and analytical only. Users are solely responsible for their decisions.</strong>
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">User Responsibilities</h2>
              <ul className="list-disc list-inside space-y-2">
                <li>Verify outputs before acting on them</li>
                <li>Use the platform lawfully and ethically</li>
                <li>Maintain account security and confidentiality</li>
                <li>Ensure you have the right to upload any documents you submit</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">Acceptable Use</h2>
              <p className="mb-2">You agree not to use {APP_NAME} to:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Harass, retaliate against, or intimidate anyone</li>
                <li>Bypass labor laws or contractual obligations</li>
                <li>Misrepresent outputs as legal rulings or final decisions</li>
                <li>Upload malicious content or attempt to disrupt the service</li>
                <li>Attempt to access another user's data</li>
                <li>Reverse engineer or abuse the system</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">Data Use</h2>
              <p>You retain ownership of uploaded content. {APP_NAME} uses it only to provide the service.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">Intellectual Property</h2>
              <p>
                {APP_NAME}'s software, interface, and branding are owned by {APP_NAME}. 
                You may not copy or resell the Service without permission.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">Disclaimers</h2>
              <p>
                {APP_NAME} is provided "as is" and "as available." We do not guarantee accuracy, completeness, 
                legal sufficiency, or suitability for any specific purpose.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">Limitation of Liability</h2>
              <p className="mb-2">
                To the maximum extent permitted by law, {APP_NAME} is not liable for employment decisions, 
                disciplinary actions, grievance outcomes, arbitration outcomes, legal claims, or financial losses 
                resulting from use of the Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">Termination</h2>
              <p>
                We may suspend or terminate accounts for violation of these Terms or abuse of the Service. 
                You may stop using the Service at any time.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">Changes</h2>
              <p>We may update these Terms periodically. Continued use means acceptance.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">Contact</h2>
              <p>
                <a href="mailto:support@cbaiq.com" className="text-primary hover:underline">
                  support@cbaiq.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
